import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  FindingFilterType,
  GetFindingsQueryVariables,
  ScoreRating,
  ScoreStateFilter,
  useGetFindingsFilterSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { usePaginate } from "~/components/pagination/usePaginate";
import { excludeFilterByKey } from "~/lib/filterUtils";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";

export type AssetSoftwareFilterVariables = Pick<
  NonNullable<GetFindingsQueryVariables["filter"]>,
  "rating" | "riskFactors" | "queryTerms"
>;
export type UseAssetSoftwareFilterContextParams = {
  scopeMrn: string;
  paginate: ReturnType<typeof usePaginate>;
  baseQueryFilters: GetFindingsQueryVariables["filter"];
};

export type UseAssetSoftwareFilterContextOutput = {
  filterVariables: AssetSoftwareFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useAssetSoftwareFilterContext({
  scopeMrn,
  paginate,
  baseQueryFilters,
}: UseAssetSoftwareFilterContextParams): UseAssetSoftwareFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const riskRatingFilterParams = searchParams.getAll("riskRating");
  const riskFactorFilterParams = searchParams.getAll("riskFactor");
  const stateFilterParam = searchParams.get("state");
  const searchFilterParams = searchParams.getAll("queryTerm");

  const riskRatingFilters = riskRatingFilterParams.every((riskFactorParam) =>
    isScoreRating(riskFactorParam),
  )
    ? riskRatingFilterParams
    : undefined;
  const riskFactorFilters = riskFactorFilterParams.length
    ? riskFactorFilterParams
    : undefined;
  const stateFilter = isScoreStateFilter(stateFilterParam)
    ? stateFilterParam
    : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  // Build merged filters for suggestions from URL params + base
  const filtersForSuggestions: GetFindingsQueryVariables["filter"] =
    useMemo(() => {
      return {
        ...baseQueryFilters,
        ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
        ...(riskFactorFilters?.length
          ? { riskFactors: { mrns: { or: riskFactorFilters } } }
          : {}),
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      };
    }, [
      baseQueryFilters,
      // dependencies reflect URL param-derived values
      // eslint-disable-next-line react-hooks/exhaustive-deps
      searchParams,
    ]);

  const filtersExcludingRating = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["rating"]),
    [filtersForSuggestions],
  );
  const riskRatingSuggestions = useAssetSoftwareRiskRatingSuggestions(
    scopeMrn,
    filtersExcludingRating,
  );
  const filtersExcludingRiskFactors = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["riskFactors"]),
    [filtersForSuggestions],
  );
  const riskFactorSuggestions = useAssetSoftwareRiskFactorSuggestions(
    scopeMrn,
    filtersExcludingRiskFactors,
  );
  const filtersExcludingState = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["state"]),
    [filtersForSuggestions],
  );
  const stateSuggestions = useAssetSoftwareStateSuggestions(
    scopeMrn,
    filtersExcludingState,
  );

  const addFilter = (param: string, value: string, multiple = false) => {
    const url = paginate.resetCursorUrl(location.href);
    if (multiple) {
      if (!url.searchParams.has(param, value)) {
        url.searchParams.append(param, value);
      }
    } else {
      url.searchParams.set(param, value);
    }
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string, value?: string) => {
    const url = paginate.resetCursorUrl(location.href);
    url.searchParams.delete(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = paginate.resetCursorUrl(location.href);
    url.searchParams.delete("riskRating");
    url.searchParams.delete("riskFactor");
    url.searchParams.delete("state");
    url.searchParams.delete("queryTerm");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    riskRatingFilters
      ? riskRatingFilters.map((riskRatingFilter) => {
          return {
            label: "Risk Rating",
            value: formatRiskRatingLabel(riskRatingFilter),
            onClick: () => removeFilter("riskRating", riskRatingFilter),
          };
        })
      : [],
    riskFactorFilters
      ? riskFactorFilters.map((riskFactorFilter) => {
          return {
            label: "Risk Factor",
            value: formatRiskFactorLabel(riskFactorFilter),
            onClick: () => removeFilter("riskFactor", riskFactorFilter),
          };
        })
      : [],
    stateFilter
      ? {
          label: "State",
          value: formatStateLabel(stateFilter),
          onClick: () => removeFilter("state"),
        }
      : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () => removeFilter("queryTerm", searchFilter),
          };
        })
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useAssetSoftwareSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
    filtersForSuggestions,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter("queryTerm", filter, true);
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Risk Rating",
      activeCount: riskRatingFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "riskRating",
        riskRatingSuggestions.suggestions,
      ),
      onMenuOpen: riskRatingSuggestions.fetchSuggestions,
    },
    {
      label: "Risk Factor",
      activeCount: riskFactorFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "riskFactor",
        riskFactorSuggestions.suggestions,
      ),
      onMenuOpen: riskFactorSuggestions.fetchSuggestions,
    },
    {
      label: "State",
      activeCount: stateFilter ? 1 : 0,
      menuOptions: quickMenuOptions("state", stateSuggestions.suggestions),
      onMenuOpen: stateSuggestions.fetchSuggestions,
    },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  const filterVariables: AssetSoftwareFilterVariables = useMemo(() => {
    return {
      ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
      ...(riskFactorFilters?.length
        ? { riskFactors: { mrns: { or: riskFactorFilters } } }
        : {}),
      ...(stateFilter ? { state: stateFilter } : {}),
      ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
    };
  }, [riskRatingFilterParams, riskFactorFilterParams, searchFilterParams]);

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useAssetSoftwareSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const categorySuggestions = useAssetSoftwareCategorySuggestions(searchInput);
  const riskRatingSuggestions = useAssetSoftwareRiskRatingSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const riskFactorSuggestions = useAssetSoftwareRiskFactorSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  const stateSuggestions = useAssetSoftwareStateSuggestions(
    scopeMrn,
    baseQueryFilters,
  );
  switch (field) {
    case "riskRating":
      return riskRatingSuggestions;
    case "riskFactor":
      return riskFactorSuggestions;
    case "state":
      return stateSuggestions;
    default:
      return categorySuggestions;
  }
}

function useAssetSoftwareCategorySuggestions(searchInput: string) {
  const riskRatingFilterGroup: FilterMenuOption = {
    field: "riskRating",
    label: "Risk Rating",
    value: "riskRating",
  };

  const riskFactorFilterGroup: FilterMenuOption = {
    field: "riskFactor",
    label: "Risk Factor",
    value: "riskFactor",
  };

  const stateFilterGroup: FilterMenuOption = {
    field: "state",
    label: "State",
    value: "state",
  };

  const suggestions = [
    riskRatingFilterGroup,
    riskFactorFilterGroup,
    stateFilterGroup,
  ].filter((s) =>
    s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
  );
  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useAssetSoftwareRiskRatingSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.Rating,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "riskRating";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreRatingValue"
            ? suggestion.value.ratingValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatRiskRatingLabel(suggestion.label),
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAssetSoftwareRiskFactorSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.RiskFactors,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "riskFactorMrn";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useAssetSoftwareStateSuggestions(
  resourceMrn: string,
  baseQueryFilters: GetFindingsQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetFindingsFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: FindingFilterType.State,
        searchText,
        resourceMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const param = "state";

  const suggestionsData =
    (data ?? previousData)?.findingsFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreStateFilterValue"
            ? suggestion.value.scoreStateValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatStateLabel(suggestion.label),
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function isScoreRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

function formatRiskRatingLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

function formatRiskFactorLabel(label: string) {
  return label.split("/").pop() || label;
}

function formatStateLabel(label: string) {
  return (
    label
      .toLowerCase()
      .split("_")
      .map((s) => upperFirst(s))
      .join(" ") || label
  );
}

function isScoreStateFilter(value: any): value is ScoreStateFilter {
  return Object.values(ScoreStateFilter).includes(value);
}
